/**
 * Cache patch helpers for targeted React Query cache updates.
 *
 * These avoid broad invalidation by surgically updating cached data
 * after mutations. Each helper is a pure function that takes a QueryClient
 * and the minimum data needed to update the cache.
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import type { TablesInsert } from "@/integrations/supabase/types";
import type { Asset, AssetTag } from "@/hooks/useAssets";

/* ── Asset Detail ────────────────────────────────── */

/**
 * Patch fields on a cached asset detail entry.
 * Falls back to invalidation if cache miss.
 */
export function patchAssetInCache(
  qc: QueryClient,
  assetId: string,
  updates: Partial<Asset>,
) {
  const key = queryKeys.asset.detail(assetId);
  const existing = qc.getQueryData<Asset>(key);
  if (existing) {
    qc.setQueryData<Asset>(key, { ...existing, ...updates });
  } else {
    qc.invalidateQueries({ queryKey: key });
  }
}

/* ── Asset List Row ──────────────────────────────── */

/**
 * Patch a single row inside every cached asset list query.
 * Iterates over all queries matching the `assets` root key
 * so every filter variant stays in sync.
 */
export function patchAssetListItem(
  qc: QueryClient,
  assetId: string,
  updates: Partial<Asset>,
) {
  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => {
      if (!old) return old;
      return old.map((a) => (a.id === assetId ? { ...a, ...updates } : a));
    },
  );
}

/**
 * Convenience: patch both detail cache and every list cache for an asset.
 */
export function patchAssetEverywhere(
  qc: QueryClient,
  assetId: string,
  updates: Partial<Asset>,
) {
  patchAssetInCache(qc, assetId, updates);
  patchAssetListItem(qc, assetId, updates);
}

/**
 * Some asset edits can change which filtered list queries should include the row.
 * When these fields change, targeted patching alone is not enough — the affected
 * filtered queries need a refetch so items can be added/removed correctly.
 */
export function assetUpdateMayAffectFilteredLists(
  updates: Partial<TablesInsert<"assets">>,
) {
  return [
    "name",
    "brand",
    "model",
    "category",
    "location_id",
    "warranty_expiry",
    "status",
  ].some((field) => field in updates);
}

/* ── Remove from list ────────────────────────────── */

/**
 * Remove an asset from all cached list queries.
 */
export function removeAssetFromListCache(
  qc: QueryClient,
  assetId: string,
) {
  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => {
      if (!old) return old;
      return old.filter((a) => a.id !== assetId);
    },
  );
}

/**
 * Remove multiple assets from all cached list queries.
 */
export function removeAssetsFromListCache(
  qc: QueryClient,
  assetIds: string[],
) {
  const idSet = new Set(assetIds);
  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => {
      if (!old) return old;
      return old.filter((a) => !idSet.has(a.id));
    },
  );
}

/* ── Tags on an asset ────────────────────────────── */

/**
 * Optimistically add a tag to a cached asset (detail + list).
 */
export function addTagToAssetCache(
  qc: QueryClient,
  assetId: string,
  tag: AssetTag,
) {
  const patch = (asset: Asset): Asset => ({
    ...asset,
    asset_tags: [...(asset.asset_tags ?? []), tag],
  });

  // Detail
  const detailKey = queryKeys.asset.detail(assetId);
  const existing = qc.getQueryData<Asset>(detailKey);
  if (existing) qc.setQueryData<Asset>(detailKey, patch(existing));

  // Lists
  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => old?.map((a) => (a.id === assetId ? patch(a) : a)),
  );
}

/**
 * Optimistically remove a tag from a cached asset (detail + list).
 */
export function removeTagFromAssetCache(
  qc: QueryClient,
  assetId: string,
  tagId: string,
) {
  const patch = (asset: Asset): Asset => ({
    ...asset,
    asset_tags: (asset.asset_tags ?? []).filter((t) => t.tag_id !== tagId),
  });

  // Detail
  const detailKey = queryKeys.asset.detail(assetId);
  const existing = qc.getQueryData<Asset>(detailKey);
  if (existing) qc.setQueryData<Asset>(detailKey, patch(existing));

  // Lists
  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => old?.map((a) => (a.id === assetId ? patch(a) : a)),
  );
}

/* ── Documents on an asset ───────────────────────── */

/**
 * Append a document to a cached asset's document list.
 */
export function addDocumentToAssetCache(
  qc: QueryClient,
  assetId: string,
  doc: Asset["asset_documents"] extends (infer D)[] | undefined ? D : never,
) {
  const patch = (asset: Asset): Asset => ({
    ...asset,
    asset_documents: [...(asset.asset_documents ?? []), doc],
  });

  const detailKey = queryKeys.asset.detail(assetId);
  const existing = qc.getQueryData<Asset>(detailKey);
  if (existing) qc.setQueryData<Asset>(detailKey, patch(existing));

  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => old?.map((a) => (a.id === assetId ? patch(a) : a)),
  );
}

/**
 * Remove a document from a cached asset by document id.
 */
export function removeDocumentFromAssetCache(
  qc: QueryClient,
  assetId: string,
  documentId: string,
) {
  const patch = (asset: Asset): Asset => ({
    ...asset,
    asset_documents: (asset.asset_documents ?? []).filter(
      (d) => d.id !== documentId,
    ),
  });

  const detailKey = queryKeys.asset.detail(assetId);
  const existing = qc.getQueryData<Asset>(detailKey);
  if (existing) qc.setQueryData<Asset>(detailKey, patch(existing));

  qc.setQueriesData<Asset[]>(
    { queryKey: queryKeys.assets.root },
    (old) => old?.map((a) => (a.id === assetId ? patch(a) : a)),
  );
}
